import React, { useMemo } from 'react';
import { StyleSheet, Pressable, View } from 'react-native';
import { Link } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import getRelativeTime from '@/constants/utils/timestamp';
import { icons as collectionIcons } from '@/components/CollectionIconsGrid';

const createStyles = (colors) => StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 16,
  },
  borderBottom: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
  },
  link: {
    flex: 1,
  },
  container: {
    padding: 12,
    cursor: 'pointer',
    borderRadius: 8,
    transition: 'background-color 0.2s ease',
  },
  content: {
    flex: 1,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    flexShrink: 1,
    minWidth: 0,
  },
  folderIcon: {
    marginRight: 8,
  },
  metaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  metaDot: {
    marginHorizontal: 6,
  },
});

export default function CollectionDetailedItem({ 
  collection, 
  showBorder = false,
  depth = 0,
}) {
  const { colors } = useTheme();
  const [isHovered, setIsHovered] = React.useState(false);
  const styles = useMemo(() => createStyles(colors), [colors]);

  return (
    <View 
      style={[
        styles.wrapper,
        showBorder && styles.borderBottom,
        depth > 0 && { paddingLeft: depth * 20 },
      ]}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link 
        href={`/collections/${collection.id}`}
        asChild
        style={styles.link}
      >
        <Pressable style={({ hovered }) => [
          styles.container,
          hovered && { backgroundColor: colors.solid_color_secondary }
        ]}>
          <View style={styles.content}>
            <View style={styles.titleRow}>
              <Icon 
                name={collectionIcons[collection?.icon]?.icon || 'folder'} 
                size={18} 
                color={colors.text_color_secondary}
                style={styles.folderIcon}
              />
              <Text size='m' color='primary' numberOfLines={1} style={styles.title}>{collection.title}</Text>
            </View>
            <View style={styles.metaContainer}>
              <Text size='xs' color='tertiary'>
                {collection?.conversationIds?.length || '0'} chat{collection?.conversationIds?.length !== 1 ? 's' : ''}
              </Text> 
              {collection?.createdAt ? (
                <>
                  <Text size='xs' color='tertiary' style={styles.metaDot}>•</Text>
                  <Text size='xs' color='tertiary'>
                    {getRelativeTime(collection.createdAt) === 'now'
                      ? 'Created just now'
                      : `Created ${getRelativeTime(collection.createdAt)} ago`}
                  </Text>
                </>
              ) : null}
            </View>
          </View>
        </Pressable>
      </Link>
    </View>
  );
}
